/* eslint-disable @typescript-eslint/no-unused-vars */
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useAppDispatch, useAppSelector } from "hooks";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FeaturesFlags } from "helpers";
import {
	getLimitedDeals,
	onSetStickyDIPData,
	onSetStickyDIPsDisplayed,
	selectCommonState,
	selectStickyDIPState
} from "redux/slices";
import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import {
	hudApi,
	offersApi,
	useGetFeatureTrackerQuery,
	useGetHudDataQuery
} from "redux/apis";
import { ShopContext } from "pages/StoreFront";
import { ISectionMenuItem } from "components/PageTopNav";
import EnlessOffer from "../EndlessOffer";
import { WaterFallSale } from "../WaterFallSale";
import StickyDIP from "components/StickyDIP";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import SectionHeader from "components/Section/SectionHeader";
import { LimitedDealsIcon } from "utils/svgIcons";
import { SwiperContainer } from "styles/general_styles";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { PurchaseOfferSingleItem, WsDialogs } from "interfaces";
import { enrichStickyDataWithHud, SortStyckyDips } from "./helpers";
import { EndlessOfferResponse } from "interfaces/endless-offer";
import { WaterfallResponse } from "interfaces/waterfall-sale";
import { usePopupsState } from "hooks/usePopupsState";
import { getTimeLeft } from "utils/time";
import { isTouchInteraction } from "utils/isTouchInteraction";
import { ELITE_DEALS_GOLD } from "pages/EliteDeals/helpers/colors";
import { SectionSource } from "interfaces/web-store/section-source";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";

const swiperParams: SwiperOptions = {
	autoplay: {
		enabled: true,
		delay: 7000,
		pauseOnMouseEnter: true,
		disableOnInteraction: false
	},
	modules: [Autoplay, Navigation, Pagination],
	navigation: true,
	pagination: {
		clickable: true,
		dynamicBullets: true,
		dynamicMainBullets: 4
	},
	//loop: false,
	rewind: true,
	slidesPerView: 1,
	spaceBetween: 30,
	slidesOffsetAfter: 0
};

export const LimitedDealsSection = ({
	from = "shop"
}: {
	from?: SectionSource;
}) => {
	const { stack } = usePopUpsV2();
	const dispatch = useAppDispatch();
	const { isAnyPopupOpen } = usePopupsState();
	const [flag, setFlag] = useState<boolean>(false);
	const { isFeatureFlagEnabled } = useEnvContext();
	const swiperRef = useRef<SwiperRef | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { sectionMenuData = [] } = useContext(ShopContext);
	const isElite = from === "elite";
	const { authToken, userTracker, nowTime } = useWebStoreContext();
	const { displayLimitedDealsDot } = useAppSelector(selectCommonState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [swiperSlides, setSwiperSlides] = useState<Array<JSX.Element>>([]);
	const { data: stickyData } = useAppSelector(selectStickyDIPState);
	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});
	const { data: cachedHudData } = hudApi.endpoints.getHudData.useQueryState("");
	const { data: queriedHudData } = useGetHudDataQuery("", {
		skip: !!cachedHudData || !authToken?.value
	});
	const hudData = cachedHudData ?? queriedHudData;

	const isWaterfallSaleActive = () => {
		return (
			!!authToken?.value && isFeatureFlagEnabled(FeaturesFlags.WATERFALL_SALES)
		);
	};
	const isEndlessOfferActive = () => {
		return (
			!!authToken?.value && isFeatureFlagEnabled(FeaturesFlags.ENDLESS_OFFER)
		);
	};

	const isStickyDipActive = () => {
		return !isElite && isFeatureFlagEnabled(FeaturesFlags.STICKY_DIPS);
	};

	const shouldShowWaterfall = () => {
		if (!waterfallData?.sessionTracker) {
			return false;
		}
		const isWaterfallElite = waterfallData.sessionTracker.isElite ?? false;
		return isElite ? isWaterfallElite : !isWaterfallElite;
	};

	const shouldShowEndless = () => {
		if (!EndlessOfferData?.tracker) {
			return false;
		}
		const isEndlessElite = EndlessOfferData.tracker.isElite ?? false;
		return isElite ? isEndlessElite : !isEndlessElite;
	};

	const { data: EndlessOfferData }: { data?: EndlessOfferResponse } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: "endless-offer",
				isAuthenticated: !!authToken?.value
			},
			{ skip: !isEndlessOfferActive() }
		);
	const { data: waterfallData }: { data?: WaterfallResponse } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: "waterfall-sale",
				isAuthenticated: !!authToken?.value
			},
			{ skip: !isWaterfallSaleActive() }
		);

	const section = sectionMenuData.find((item: ISectionMenuItem) => {
		if (isWaterfallSaleActive() && waterfallData?.sessionTracker) {
			return item.name === "waterfall_sale";
		}
		if (isEndlessOfferActive() && EndlessOfferData?.tracker) {
			return item.name === "endless_offer";
		}
		if (isStickyDipActive() && stickyData) {
			return item.name === "limited_deals";
		}
		return false;
	});

	const handleResize = () => {
		swiperRef?.current?.swiper?.update();
	};

	const eventSource = isElite ? "vip_shop" : "limited_deal";

	const analytics = (index: number) => {
		if (swiperSlides?.[index]?.props?.data) {
			reportEvent("offer_rendered", {
				source: eventSource,
				offer_id: swiperSlides[index].props.data[0]?.id,
				reward_id: swiperSlides[index].props.data[0]?.rewardId,
				price: swiperSlides[index].props.data[0]?.price,
				loyalty_points: swiperSlides[index].props.data[0]?.loyaltyPointReward,
				offer_ends_time: swiperSlides[index].props.data[0]?.timerExpirationDate,
				image_url: swiperSlides[index].props.data[0]?.dip?.imageUrl
			});
			const timeLeft = getTimeLeft(
				nowTime,
				swiperSlides[index].props.data[0]?.timerExpirationDate,
				"seconds"
			);
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "offer_rendered",
				event_type: "limited_deals_impression",
				action: "offer_rendered",
				source: eventSource,
				offer_id: swiperSlides?.[index]?.props?.data?.[0]?.id,
				reward_id: swiperSlides?.[index]?.props?.data?.[0]?.rewardId,
				price_in_cents: Math.round(
					swiperSlides?.[index]?.props?.data?.[0]?.price * 100
				),
				value_int: swiperSlides?.[index]?.props?.data?.[0]?.loyaltyPointReward,
				time_left: timeLeft,
				link_url: swiperSlides?.[index]?.props?.data?.[0]?.dip?.imageUrl
			});
		} else {
			if (isWaterfallSaleActive() && waterfallData?.sessionTracker) {
				const tierStatus = waterfallData?.sessionTracker?.tierStatuses.filter(
					(ts) => !ts.completed
				)?.[0];
				reportEvent("offer_rendered", {
					source: eventSource,
					offer_id: waterfallData?.sessionTracker?.saleId,
					reward_id: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) =>
							offer.id === tierStatus?.tier?.offerId
					)?.rewardId,
					sale_id: waterfallData?.sessionTracker?.saleId,
					price: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) =>
							offer.id === tierStatus?.tier?.offerId
					)?.price,
					loyalty_points: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) =>
							offer.id === tierStatus?.tier?.offerId
					)?.loyaltyPointReward,
					offer_ends_time: waterfallData?.sessionTracker?.endTime,
					bannertext: waterfallData?.sessionTracker?.title,
					image_url: waterfallData?.sessionTracker?.backgroundImageUrl
				});
				const timeLeft = getTimeLeft(
					nowTime,
					waterfallData?.sessionTracker?.endTime,
					"seconds"
				);
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "offer_rendered",
					event_type: "limited_deals_impression",
					action: "offer_rendered",
					source: eventSource,
					offer_id: waterfallData?.sessionTracker?.saleId,
					reward_id: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) =>
							offer?.id === tierStatus?.tier?.offerId
					)?.rewardId,
					segment_id: waterfallData?.sessionTracker?.saleId,
					price_in_cents: Math.round(
						purchaseOffers?.offers?.find(
							(offer: PurchaseOfferSingleItem) =>
								offer?.id === tierStatus?.tier?.offerId
						)?.price * 100
					),
					value_int: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) =>
							offer.id === tierStatus?.tier?.offerId
					)?.loyaltyPointReward,
					time_left: timeLeft,
					tag_text: waterfallData?.sessionTracker?.title,
					link_url: waterfallData?.sessionTracker?.backgroundImageUrl
				});
			} else if (isEndlessOfferActive() && EndlessOfferData?.tracker) {
				const node = EndlessOfferData?.tracker?.nodes.filter(
					(node) => !node.completionTime
				)?.[0];
				reportEvent("offer_rendered", {
					source: eventSource,
					offer_id: node?.offerId,
					event_id: EndlessOfferData?.tracker?.eventId,
					reward_id: node?.rewardId,
					price:
						purchaseOffers?.offers.find(
							(offer: PurchaseOfferSingleItem) => offer?.id === node?.offerId
						)?.price ?? 0,
					loyalty_points:
						purchaseOffers?.offers.find(
							(offer: PurchaseOfferSingleItem) => offer?.id === node?.offerId
						)?.loyaltyPointReward ?? 0,
					offer_ends_time: EndlessOfferData?.tracker?.endTime,
					bannertext: EndlessOfferData?.tracker?.titleImageUrl,
					image_url: EndlessOfferData?.tracker?.themeBackgroundImageUrl
				});
				const timeLeft = getTimeLeft(
					nowTime,
					EndlessOfferData?.tracker?.endTime,
					"seconds"
				);
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "offer_rendered",
					event_type: "endless_offer",
					action: "offer_rendered",
					source: eventSource,
					offer_id: node?.offerId,
					reward_id: node?.rewardId,
					segment_id: EndlessOfferData?.tracker?.eventId,
					price_in_cents: Math.round(
						purchaseOffers?.offers?.find(
							(offer: PurchaseOfferSingleItem) => offer?.id === node?.offerId
						)?.price * 100
					),
					value_int: purchaseOffers?.offers.find(
						(offer: PurchaseOfferSingleItem) => offer.id === node?.offerId
					)?.loyaltyPointReward,
					time_left: timeLeft,
					tag_text: EndlessOfferData?.tracker?.titleImageUrl,
					link_url: EndlessOfferData?.tracker?.themeBackgroundImageUrl,
					node_type: node?.isSuperOffer ? "golden" : "normal",
					node_type2: node?.nodeIndex.toString(),
					confignodecount: EndlessOfferData?.tracker?.configNodeCount,
					progress_status: node?.nodeBadgeText,
					theme_id: EndlessOfferData?.tracker?.themeId
				});
			}
		}
	};

	const setAutoplay = (enabled: boolean) => {
		const swiper = swiperRef.current?.swiper;

		if (!swiper || swiper.destroyed) {
			return;
		}

		if (enabled) {
			if (!swiper.autoplay.running) {
				swiper.autoplay.paused = false;
				swiper.autoplay.start();
			}
			return;
		}

		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
		}
		swiper.autoplay.paused = false;
	};

	useEffect(() => {
		setAutoplay(!isAnyPopupOpen);
	}, [isAnyPopupOpen, swiperSlides.length]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				dispatch(getLimitedDeals());
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	useEffect(() => {
		if (!flag && swiperSlides?.length > 0) {
			analytics(0);
			setFlag(true);
		}
	}, [swiperSlides?.length]);

	useEffect(() => {
		if (!userTracker) {
			dispatch(onSetStickyDIPData(undefined));
			setSwiperSlides([]);
		} else {
			dispatch(getLimitedDeals());
		}
	}, [userTracker]);

	useEffect(() => {
		const generateSwiperContent = () => {
			// * Having the LD section populating during tutorial caused a blip in step 1 Tutorial v2
			// * So we skip generating the swiper content if the tutorial is active
			if (stack?.[0]?.variant === "tutorial-v2") {
				return;
			}
			const swiperContent: Array<JSX.Element> = [];
			if (isWaterfallSaleActive() && shouldShowWaterfall()) {
				swiperContent.push(
					<WaterFallSale key={waterfallData?.sessionTracker.id} />
				);
			}
			if (isEndlessOfferActive() && shouldShowEndless()) {
				swiperContent.push(
					<EnlessOffer key={EndlessOfferData?.tracker.eventId} from={from} />
				);
			}
			if (isStickyDipActive() && stickyData) {
				const enrichedStickyData = enrichStickyDataWithHud(
					stickyData,
					hudData?.data?.dipConfigs
				);
				const sortedStickyDataByDates = SortStyckyDips(
					enrichedStickyData,
					swiperContent.length
				);
				Object.values(Object.fromEntries(sortedStickyDataByDates)).forEach(
					(sd) => {
						if (sd?.[0]?.dip) {
							swiperContent.push(
								<StickyDIP data={sd} key={sd[0].campaignGroup} />
							);
						}
					}
				);
				dispatch(
					onSetStickyDIPsDisplayed(Object.fromEntries(sortedStickyDataByDates))
				);
			}
			setSwiperSlides([...swiperContent]);
		};
		generateSwiperContent();
	}, [
		stickyData,
		hudData,
		EndlessOfferData,
		waterfallData,
		stack?.[0]?.variant
	]);

	useEffect(() => {
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	if (!purchaseOffers || swiperSlides.length === 0) {
		return <></>;
	}

	return (
		<section
			ref={section?.sectionRef}
			style={{ marginBottom: "10px" }}
			id="Limited Deals"
		>
			<SectionHeader
				text="LIMITED DEALS"
				icon={<LimitedDealsIcon />}
				showRedDot={!isElite && displayLimitedDealsDot}
				sx={{
					marginBottom: { xs: "0px", sm: "10px" },
					...(isElite
						? {
								color: ELITE_DEALS_GOLD,
								"& svg": { fill: ELITE_DEALS_GOLD }
						  }
						: {})
				}}
			/>
			<SwiperContainer>
				{swiperSlides && (
					<Swiper
						ref={swiperRef}
						{...swiperParams}
						className="tiki-swiper"
						onTouchStart={(_, event) => {
							if (isTouchInteraction(event)) {
								setAutoplay(false);
							}
						}}
						onTouchEnd={(_, event) => {
							if (isTouchInteraction(event)) {
								setAutoplay(!isAnyPopupOpen);
							}
						}}
						onSlideChangeTransitionStart={(e) => analytics(e.realIndex)}
					>
						{swiperSlides.map((ss, i) => (
							<SwiperSlide key={`${ss.key}-${i}`}>{ss}</SwiperSlide>
						))}
					</Swiper>
				)}
			</SwiperContainer>
		</section>
	);
};
